'use strict'

import { gsap, TweenMax } from 'gsap';
import { CSSRulePlugin } from 'gsap/dist/CSSRulePlugin';
import { isDesktop, isTouchScreen, calculateContainerPositionFromSide } from './helpers';
import customCursors from './custom-cursors';

gsap.registerPlugin(CSSRulePlugin);

/**
 * Functionality for the timeline element at the left or right side of the screen. 
 * 
 * Note that some functionality are closely related (for example activation of the year on timeline) to 
 * year scroller so this TimelineModule is used on year scroller.
 */
const timeline = {

  /**
   * Dom related elements and classes
   */
  dom: {
    elements: {
      timeline: {
        left: {
          years: Array.from(document.querySelectorAll('.timeline.left .year'))
        },
        right: {
          years: Array.from(document.querySelectorAll('.timeline.right .year'))
        }
      }
    },
    classes: {
      INTERACTABLE: 'interactable',
      NOT_INTERACTABLE: 'not-interactable'
    }
  },

  /**
   * Gets all timeline elements.
   * 
   * @param {string} timelineClassSelector Adds this suffix to selector. With this specific timeline could queried.
   * @returns {Object} Timeline elements
   */
  getAllTimelines: function(timelineClassSelector = '') {
    return document.querySelectorAll(`.timeline${timelineClassSelector}`);
  },

  /**
   * Gets all year elements inside timeline elements.
   * 
   * @param {string} timelineClassSelector Adds this suffix to selector. With this specific timeline could queried.
   * @returns {Object} Year elements inside timeline
   */
  getAllTimelineYears: function(timelineClassSelector = '') {
    return document.querySelectorAll(`.timeline${timelineClassSelector} .year`);
  },

  /**
   * Toggles interactable class of this timeline.
   *  
   * @param {Object} timelineElement Timeline dom element
   */
  toggleInteractableClass: function(timelineElement) {
    timelineElement.classList.toggle('interactable');
  },

  /**
   * Sets position for left and right timeline.
   */
  setPositionOfTimelines: function() {

    const leftTimeline = this.getAllTimelines('.left')[0];
    const rightTimeline = this.getAllTimelines('.right')[0];
    const positionFromSide = calculateContainerPositionFromSide();
    const positionFromTop = this.calculatePositionFromTop(leftTimeline);
    leftTimeline.style.left = `${positionFromSide}px`;
    leftTimeline.style.top = `${positionFromTop}px`;
    rightTimeline.style.right = `${positionFromSide}px`;
    rightTimeline.style.top = `${positionFromTop}px`;
  },

  /**
   * Calculates position from fixed top so timeline is center. 
   * 
   * @param {Object} timeline Timeline dom element
   * @returns {Number} Position from top.
   */
  calculatePositionFromTop: function(timeline) {
    const fromTop = (window.innerHeight - timeline.offsetHeight) / 2;
    return fromTop > 0 ? fromTop : 0;
  },

  /**
   * Updates given year as an active year on the timeline element.
   * 
   * @param {string} year Year number.
   */
  updateTimelineYear: function(year) {

    // Set all years as non-active in timelines
    document.querySelectorAll('.timeline .year').forEach(function(item,index) {
      item.classList.remove('active')
    });

    // Set current year active in timeline
    document.querySelectorAll('.timeline .year[data-year="'+ year +'"]').forEach(function(item,index){
      item.classList.add('active');                
    })
  },

  /**
   * Animates specific timeline and adds it to given timeline.
   * 
   * @param {string} cssClass Class suffix of specific timeline
   * @param {string} startLabel Start position (label on the given timeline object) of timeline element animation.
   * @param {Object} timeline GSAP Timeline object https://greensock.com/docs/v3/GSAP/Timeline
   */
  animateTimeline: function(cssClass = '', startLabel, timeline) {

    const timelineElement = document.querySelector(`.timeline.${cssClass}`);
    const markers = Array.from(document.querySelectorAll(`.timeline.${cssClass} .year .marker`)).reverse()

    // Fade in markers
    timeline.fromTo(markers,
      {
        opacity: '0',
      },
      {
        opacity: '1',
        duration: 0.3,
        stagger: 0.01,
        ease: 'power4.in'
      },
      startLabel
    );

    // Grow markers
    timeline.fromTo(markers,
      {
        width: '0%',
      },
      {
        width: '100%',
        duration: 0.5,
        stagger: 0.01,
        ease: 'power4.in'
      },
      startLabel
    );

    const textWrappers = Array.from(document.querySelectorAll(`.timeline.${cssClass} .year.highlight .text-wrapper`)).reverse();

    // Fade in year text
    timeline.fromTo(textWrappers,
      {
        opacity: '0'
      },
      {
        opacity: '1',
        duration: 0.8,
        stagger: 0.1,
        ease: 'power4.out',
      },
      startLabel + '+=0.3'
    );

    const makeTimelineInteractable = (element) => {
      this.toggleInteractableClass(element);
      customCursors.setElementCursorType('point', [element]);
    }

    // Bounce year text
    timeline.fromTo(textWrappers,
      {
        margin: '0 -5px'
      },
      {
        margin: '0 5px',
        duration: 0.7,
        stagger: 0.1,
        ease: 'power4.out',
        onComplete: makeTimelineInteractable,
        onCompleteParams: [timelineElement]
      },
      startLabel + '+=0.5'
    );

  },

  /**
   * Creates timeline year element animation on mouse enter.
   * 
   * @param {Object} event onMouseEnter Javascript event object
   */
  onMouseEnterYear: function(event) {

    const parentTimelineElement = this.parentElement;

    if (parentTimelineElement.classList.contains('interactable') && isDesktop() && !this.classList.contains('highlight')) {

      // Right timeline year
      if (parentTimelineElement.classList.contains('right')) {

        TweenMax.fromTo(this.firstElementChild,
          {
            opacity: 0,
            right: '-5px'
          },
          {
            opacity: 1,
            right: '0px',
            duration: 0.1,
            clearProps: 'right'
          }
        );

      // Left timeline year
      } else {

        TweenMax.fromTo(this.lastElementChild,
          {
            opacity: 0,
            left: '-5px'
          },
          {
            opacity: 1,
            left: '0px',
            duration: 0.1,
            clearProps: 'left'
          }
        );
      }
    }
  },

  /**
   * Creates timeline year element animation on mouse leave.
   * 
   * @param {Object} event onMouseLeaveYear Javascript event object
   */
  onMouseLeaveYear: function(event) {

    const parentTimelineElement = this.parentElement;

    if (parentTimelineElement.classList.contains('interactable') && isDesktop() && !this.classList.contains('highlight')) {

      // Right timeline year
      if (parentTimelineElement.classList.contains('right')) {

        TweenMax.fromTo(this.firstElementChild,
          {
            opacity: 1,
            right: '0px'
          },
          {
            opacity: 0,
            right: '-5px',
            duration: 0.1,
            clearProps: 'right'
          }
        );

      // Left timeline year
      } else {

        TweenMax.fromTo(this.lastElementChild,
          {
            opacity: 1,
            left: '0px'
          },
          {
            opacity: 0,
            left: '-5px',
            duration: 0.1,
            clearProps: 'left'
          }
        );
      }
    }
  },

  /**
   * Initializes module. Sets position of timelines and adds event listeners.
   */
  init: function () {

    if (document.body.classList.contains('timeline-page')) {

      // On page load set position of timeline (this is reset on every resize in /year-scroller.js file)
      this.setPositionOfTimelines();
    
      if (!isTouchScreen()) {
        // Year mouse enter and leave listeners
        this.dom.elements.timeline.left.years.map((item) => item.addEventListener('mouseenter', this.onMouseEnterYear));
        this.dom.elements.timeline.left.years.map((item) => item.addEventListener('mouseleave', this.onMouseLeaveYear));
        this.dom.elements.timeline.right.years.map((item) => item.addEventListener('mouseenter', this.onMouseEnterYear));
        this.dom.elements.timeline.right.years.map((item) => item.addEventListener('mouseleave', this.onMouseLeaveYear));
      }
    }
  }
}

module.exports = timeline;